import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useEditorStore } from "@/store/editorStore";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  Crown,
  Sparkles,
  Undo2,
  Redo2,
  Code2,
  FolderOpen,
  Eye,
  Share2,
  Download,
  Globe,
  Save,
  ChevronLeft,
  LogOut,
} from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import buildExport from "@/components/editor/exportSite";

export default function TopBar({ onOpenPublish, onOpenPreview, onOpenShare, onOpenWidget }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const projectId = useEditorStore((s) => s.projectId);
  const projectName = useEditorStore((s) => s.projectName);
  const setProjectName = useEditorStore((s) => s.setProjectName);
  const pages = useEditorStore((s) => s.pages);
  const undo = useEditorStore((s) => s.undo);
  const redo = useEditorStore((s) => s.redo);

  const isPro = user?.subscription?.is_master || user?.subscription?.tier === "pro" || user?.subscription?.tier === "pro_trial";

  const saveProject = async () => {
    if (!projectId) return;
    try {
      await api.patch(`/projects/${projectId}`, { name: projectName, pages });
      toast.success("Saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  // Wire all global window.* functions per spec
  useEffect(() => {
    window.triggerLayoutSave = saveProject;
    window.openProjectsDashboardWindow = () => nav("/dashboard");
    window.openInteractiveDevicePreview = () => onOpenPreview();
    window.triggerProjectPublishToWeb = () => onOpenPublish();
    window.openSocialShareGateway = () => onOpenShare();
    window.openWidgetModal = () => onOpenWidget();
    window.commitWidgetCode = (html) => {
      const targetPageId = useEditorStore.getState().activePageId || pages[0]?.id;
      useEditorStore.getState().addElement(targetPageId, {
        type: "text",
        x: 80,
        y: 80,
        w: 480,
        h: 120,
        text: html || "Widget HTML",
        fontSize: 16,
        color: "#111827",
      });
      toast.success("Widget inserted");
    };
    window.triggerZipExport = async () => {
      if (!isPro) {
        toast.error("ZIP export is a Pro feature. Upgrade your plan.");
        nav("/upgrade");
        return;
      }
      try {
        await buildExport(useEditorStore.getState());
        toast.success("ZIP downloaded");
      } catch (e) {
        toast.error("Export failed");
      }
    };
    window.executeSignOut = async () => {
      await logout();
      nav("/login");
    };
    // keyboard shortcuts
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "z" && !e.shiftKey) {
        e.preventDefault(); undo();
      } else if ((e.metaKey || e.ctrlKey) && (e.key.toLowerCase() === "y" || (e.shiftKey && e.key.toLowerCase() === "z"))) {
        e.preventDefault(); redo();
      } else if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
        e.preventDefault(); saveProject();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pages, projectId, projectName, isPro]);

  const Btn = ({ children, onClick, testId, title, disabled }) => (
    <button
      onClick={onClick}
      title={title}
      disabled={disabled}
      data-testid={testId}
      className="h-9 px-2.5 rounded-md text-sm text-gray-700 hover:bg-gray-100 inline-flex items-center gap-1.5 disabled:opacity-40"
    >
      {children}
    </button>
  );

  return (
    <header className="h-14 bg-white border-b border-gray-200 flex items-center justify-between px-3 z-50 shrink-0" data-testid="editor-topbar">
      <div className="flex items-center gap-2 min-w-0">
        <button
          className="w-9 h-9 rounded-md hover:bg-gray-100 flex items-center justify-center"
          onClick={() => nav("/dashboard")}
          title="Back to dashboard"
          data-testid="editor-back-button"
        >
          <ChevronLeft className="w-5 h-5" strokeWidth={1.6} />
        </button>
        <div className="w-8 h-8 rounded-md bg-[#002FA7] flex items-center justify-center text-white">
          <Sparkles className="w-4 h-4" strokeWidth={1.6} />
        </div>
        <span className="hidden md:inline font-display font-bold text-sm tracking-tight whitespace-nowrap">CreativeHub Website Pro Builder</span>
        <span className="text-gray-300 mx-1 hidden md:inline">·</span>
        <Input
          value={projectName}
          onChange={(e) => setProjectName(e.target.value)}
          className="h-8 max-w-[240px] text-sm border-transparent hover:border-gray-200 focus:border-gray-200"
          data-testid="project-name-input"
        />
      </div>

      <div className="flex items-center gap-1">
        <Btn onClick={undo} testId="topbar-undo" title="Undo (Ctrl+Z)"><Undo2 className="w-4 h-4" /></Btn>
        <Btn onClick={redo} testId="topbar-redo" title="Redo (Ctrl+Y)"><Redo2 className="w-4 h-4" /></Btn>
        <Btn onClick={() => window.openWidgetModal()} testId="topbar-widget" title="Insert HTML widget"><Code2 className="w-4 h-4" /></Btn>
        <Btn onClick={() => window.openProjectsDashboardWindow()} testId="topbar-projects" title="Projects dashboard"><FolderOpen className="w-4 h-4" /></Btn>
        <Btn onClick={() => window.openInteractiveDevicePreview()} testId="topbar-preview" title="Device preview"><Eye className="w-4 h-4" /></Btn>
        <Btn onClick={() => window.openSocialShareGateway()} testId="topbar-share" title="Share design"><Share2 className="w-4 h-4" /></Btn>
        <Btn onClick={() => window.triggerZipExport()} testId="topbar-export" title="Export ZIP (Pro)"><Download className="w-4 h-4" /></Btn>
        <Btn onClick={saveProject} testId="topbar-save" title="Save (Ctrl+S)"><Save className="w-4 h-4" /></Btn>

        {!user?.subscription?.is_master && (
          <button
            className="ch-upgrade rounded-md px-3 py-2 text-sm inline-flex items-center gap-1.5 ml-1"
            onClick={() => nav("/upgrade")}
            data-testid="topbar-upgrade-button"
          >
            <Crown className="w-4 h-4" />
            Upgrade your plan
          </button>
        )}

        <button
          className="ml-2 bg-[#002FA7] hover:bg-blue-800 text-white h-9 px-4 rounded-md text-sm font-semibold inline-flex items-center gap-1.5"
          onClick={() => window.triggerProjectPublishToWeb()}
          data-testid="topbar-publish-button"
        >
          <Globe className="w-4 h-4" /> Publish
        </button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="ml-1 w-9 h-9 rounded-full bg-gray-100 text-gray-700 flex items-center justify-center text-sm font-bold" data-testid="topbar-user-menu">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem disabled className="text-xs">{user?.email}</DropdownMenuItem>
            <DropdownMenuItem onClick={() => nav("/upgrade")}>Billing & Plans</DropdownMenuItem>
            <DropdownMenuItem onClick={() => window.executeSignOut()}>
              <LogOut className="w-4 h-4 mr-2" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
